import { ApplicationStatus, Qualification } from './academic';
import { PaymentStatus } from './payment';

export type Gender = 'MALE' | 'FEMALE' | 'OTHER';
export type OlAlType = 'LOCAL' | 'LONDON';
export type ALStream = 'MATHEMATICS' | 'SCIENCE' | 'COMMERCE' | 'ARTS' | 'IT' | 'OTHER';
export type MaritalStatus = 'SINGLE' | 'MARRIED' | 'OTHER';
export type EnglishTestType = 'IELTS' | 'PTE' | 'TOEFL' | 'DUOLINGO' | 'NONE';
export type VisaType = 'STUDENT' | 'TOURIST' | 'WORK' | 'VISIT' | 'OTHER';
export type FundingSource = 'SELF' | 'LOAN' | 'SPONSOR' | 'PARENTS' | 'SCHOLARSHIP' | 'EMPLOYER' | 'OTHER';

export interface SubjectResult {
  subject: string;
  grade?: string;
}

export interface HealthDeclarationAnswer {
  key: string;
  answer?: 'YES' | 'NO';
  statement?: string;
}

/** Single source of truth for the Health Declaration condition list/order
 * on the frontend — must exactly mirror HEALTH_DECLARATION_ITEMS in the
 * backend's documentTemplates/healthDeclaration.template.ts (wording/order
 * taken verbatim from the original paper form; the two lists are kept in
 * sync by hand since frontend/backend don't share code). */
export const HEALTH_DECLARATION_CONDITIONS: Array<{ key: string; label: string; group?: string }> = [
  { key: 'tuberculosis', label: 'Tuberculosis' },
  { key: 'hepatitisB', label: 'Hepatitis B' },
  { key: 'hepatitisC', label: 'Hepatitis C' },
  { key: 'hiv', label: 'HIV' },
  { key: 'opiates', label: '1. Opiates', group: 'Drug use/abuse of:' },
  { key: 'cannabinoids', label: '2. Cannabinoids', group: 'Drug use/abuse of:' },
  { key: 'amphetamine', label: '3. Amphetamine', group: 'Drug use/abuse of:' },
  { key: 'methamphetamine', label: '4. Methamphetamine', group: 'Drug use/abuse of:' },
  { key: 'std', label: 'Sexually Transmitted Diseases' },
  { key: 'congenitalDisorder', label: 'Congenital or Inherited Disorder' },
  { key: 'cancer', label: 'Cancer' },
  { key: 'epilepsy', label: 'Epilepsy' },
  { key: 'psychiatricIllness', label: 'Psychiatric Illness' },
  { key: 'otherIllness', label: 'Other illness' },
];

export interface StudentListItem {
  id: number;
  /** TEMP-STU… until registered with an approved Registration Fee, then STU…. */
  studentNumber: string;
  applicationNumber: string;
  /** The temporary references, kept as history once permanent numbers are issued. */
  temporaryStudentNumber?: string | null;
  temporaryApplicationNumber?: string | null;
  studentNumberAssignedAt?: string | null;
  isDraft: boolean;
  lastCompletedStep: number;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string;
  dateOfBirth: string | null;
  gender: Gender | null;
  passportNumber: string | null;
  passportExpiryDate: string | null;
  address: string | null;
  fatherName: string | null;
  fatherPhone: string | null;
  motherName: string | null;
  motherPhone: string | null;
  guardianName: string | null;
  guardianRelationship: string | null;
  guardianPhone: string | null;
  guardianEmail: string | null;
  emergencyContactName: string | null;
  emergencyContactPhone: string | null;
  emergencyContactRelationship: string | null;
  createdAt: string;
  country: { id: number; name: string } | null;
  branch: { id: number; name: string } | null;
  registeredBy: { id: number; firstName: string; lastName: string };
  lead: { id: number; leadNumber: string } | null;
  // Student Portal (Phase 67) — set once portal access has been
  // provisioned (automatically on lead-conversion, or via the manual
  // "Send portal invite" action); null until then.
  userId: number | null;
  /** Alumni Management (Phase 105) — set by Mark as Graduated only. */
  graduatedAt?: string | null;
  /** When the student became registered (null while a draft). */
  registeredAt?: string | null;
  /** Official dropout (Management Reports): date and reason, recorded by
   * management or admissions staff. */
  droppedOutAt?: string | null;
  dropoutReason?: string | null;
  graduatedBy?: { id: number; firstName: string; lastName: string } | null;
  alumni?: { id: number; alumniNumber: string; archivedAt: string | null } | null;

  // Personal details (Step 2)
  otherNames: string | null;
  preferredName: string | null;
  nicNumber: string | null;
  passportIssueDate: string | null;
  countryOfBirth: { id: number; name: string } | null;
  maritalStatus: MaritalStatus | null;
  whatsappNumber: string | null;
  telephone: string | null;
  city: string | null;
  postalCode: string | null;
  province: string | null;
  nationalityCountry: { id: number; name: string } | null;

  // Academic details (Step 3)
  schoolName: string | null;
  olType: OlAlType | null;
  olYear: number | null;
  olResults: SubjectResult[] | null;
  alType: OlAlType | null;
  alYear: number | null;
  alStream: ALStream | null;
  alResults: SubjectResult[] | null;
  overallGpa: string | null;

  // Preferred study (Step 5)
  preferredUniversity: { id: number; name: string; country: { id: number; name: string } } | null;
  alternativeUniversity: { id: number; name: string } | null;
  preferredProgram: { id: number; name: string } | null;
  preferredFaculty: { id: number; name: string } | null;
  preferredQualificationLevel: { id: number; name: string } | null;
  preferredIntake: { id: number; name: string } | null;
  // Student Application correction: replaces the "Semester / Intake"
  // dropdown with free text; preferredIntake above stays for historical
  // records (never removed) but the current wizard reads/writes this.
  preferredIntakeLabel: string | null;
  preferredCity: string | null;
  alternativeCountry: { id: number; name: string } | null;
  budget: string | null;
  budgetCurrency: { id: number; code: string; symbol: string | null } | null;
  scholarshipRequired: boolean;
  englishTestType: EnglishTestType | null;
  englishTestScore: string | null;
  englishTestDate: string | null;

  // Parents/guardian additions (Step 6)
  fatherOccupation: string | null;
  fatherOrganization: string | null;
  fatherOfficePhone: string | null;
  fatherEmail: string | null;
  fatherWhatsapp: string | null;
  fatherAddress: string | null;
  fatherIsSponsor: boolean | null;
  motherOccupation: string | null;
  motherOrganization: string | null;
  motherOfficePhone: string | null;
  motherEmail: string | null;
  motherWhatsapp: string | null;
  motherAddress: string | null;
  motherIsSponsor: boolean | null;
  guardianWhatsapp: string | null;
  guardianAddress: string | null;
  guardianIsSponsor: boolean | null;

  // Visa history (Step 7)
  visaAppliedBefore: boolean | null;
  visaRefused: boolean | null;
  visaRefusedCountry: { id: number; name: string } | null;
  visaType: VisaType | null;
  visaRefusalReason: string | null;
  visaComments: string | null;
  travelHistory: string | null;

  // Financial information (Step 8)
  monthlyIncome: string | null;
  annualIncome: string | null;
  fundingMethod: FundingSource | null;
  sponsorDetails: string | null;

  // Declaration (Step 10)
  studentAgreementAccepted: boolean;
  privacyConsentAccepted: boolean;
  marketingConsentAccepted: boolean;
  termsAcceptedAt: string | null;
  studentSignatureName: string | null;
  studentSignatureAt: string | null;
  guardianSignatureName: string | null;
  guardianSignatureAt: string | null;

  // Health Declaration Form (Step 10)
  healthDeclarationRequired: boolean;
  healthDeclarationAnswers: HealthDeclarationAnswer[] | null;
  guardianPassportOrNic: string | null;

  campaign: { id: number; name: string } | null;

  // Student Application Form PDF header (Document PDF Templates correction).
  academicYear: string | null;
  semester: string | null;
}

export type DocumentVerificationStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

export interface StudentDocument {
  id: number;
  studentId: number;
  originalName: string;
  mimeType: string;
  sizeBytes: number;
  expiryDate: string | null;
  createdAt: string;
  verificationStatus: DocumentVerificationStatus;
  rejectionReason: string | null;
  verifiedAt: string | null;
  documentType: { id: number; name: string; requiresExpiry: boolean };
  uploadedBy: { id: number; firstName: string; lastName: string };
  verifiedBy: { id: number; firstName: string; lastName: string } | null;
  documentTypeId: number | null;
  version: number;
  previousVersionId: number | null;
  isLatest: boolean;
  /** The student's name for an Other document (Phase 103); else null. */
  customName?: string | null;

  // Document PDF Templates correction — a GENERATED row is otherwise an
  // ordinary StudentDocument (same version/isLatest/preview/download/RBAC),
  // just produced by a template instead of a multer upload.
  source: 'UPLOADED' | 'GENERATED';
  templateKey: string | null;
  templateVersion: string | null;
  generatedBy: { id: number; firstName: string; lastName: string } | null;

  // Student-Wise Payment Customization (Phase 52) — only ever populated
  // for templateKey='ADMISSION_AGREEMENT'. `sends` is the full Generated
  // ≠ Sent history (§25); an empty array means "generated, never sent".
  feeVersion: { id: number; versionNumber: number } | null;
  sends: DocumentSend[];
}

/** Admissions document-requirement correction (Phase 60) — required
 * documents come from Master Data's CourseDocumentRequirement for this
 * student's resolved university/programme, never the full DocumentType
 * catalog. `items`/`summary` are both null only when no course can be
 * resolved at all (no Application yet and no preferredProgramId). */
export type DocumentChecklistStatus = 'MISSING' | 'PENDING' | 'APPROVED' | 'REJECTED';

export interface DocumentChecklistItem {
  documentTypeId: number;
  documentTypeName: string;
  isMandatory: boolean;
  status: DocumentChecklistStatus;
  satisfied: boolean;
  studentDocumentId: number | null;
  rejectionReason: string | null;
}

export interface DocumentChecklistSummary {
  requiredTotal: number;
  approved: number;
  pendingApproval: number;
  rejected: number;
  missing: number;
  overall: 'APPROVED' | 'PENDING' | 'REJECTED';
}

export interface StudentDocumentChecklist {
  items: DocumentChecklistItem[] | null;
  summary: DocumentChecklistSummary | null;
}

export type DocumentSendMethod = 'EMAIL' | 'OTHER';

export interface DocumentSend {
  id: number;
  sentAt: string;
  method: DocumentSendMethod;
  recipient: string;
  note: string | null;
  sentBy: { id: number; firstName: string; lastName: string };
}

export type DocumentTemplateKey =
  | 'STUDENT_APPLICATION_FORM'
  | 'HEALTH_DECLARATION'
  | 'UNIVERSITY_APPLICATION'
  | 'ADMISSION_AGREEMENT';

// Editable document fields (Phase 91) — the "Edit Document Details" step
// for the two templates that have one. Mirrors the backend's
// documentTemplates/editableFields.ts.
export type EditableDocumentTemplateKey = 'UNIVERSITY_APPLICATION' | 'ADMISSION_AGREEMENT' | 'HEALTH_DECLARATION';
export const EDITABLE_DOCUMENT_TEMPLATE_KEYS: readonly DocumentTemplateKey[] = [
  'UNIVERSITY_APPLICATION',
  'ADMISSION_AGREEMENT',
  'HEALTH_DECLARATION',
];
/** Editable templates that place the student's saved signature image — the
 * Health Declaration prints typed signature names instead (Phase 109). */
export const SIGNATURE_IMAGE_TEMPLATE_KEYS: readonly EditableDocumentTemplateKey[] = ['UNIVERSITY_APPLICATION', 'ADMISSION_AGREEMENT'];

export type DocumentFieldSource = 'STUDENT' | 'MASTER_DATA' | 'TEMPLATE_DEFAULT' | 'BUILT_IN' | 'NONE';

export interface DocumentField {
  key: string;
  label: string;
  group: string;
  /** TEMPLATE: periodic template text, can be saved as the university
   * default. DOCUMENT: this document only, never written to the student. */
  scope: 'TEMPLATE' | 'DOCUMENT';
  input: 'text' | 'textarea' | 'select';
  maxLength: number;
  options?: Array<{ value: string; label: string }>;
  helperText?: string;
  defaultValue: string;
  source: DocumentFieldSource;
  baseValue?: string;
  baseSource?: DocumentFieldSource;
}

export interface DocumentFieldForm {
  templateKey: EditableDocumentTemplateKey;
  universityId: number | null;
  universityName: string;
  templateDefaults: { updatedAt: string; updatedBy: { id: number; firstName: string; lastName: string } } | null;
  fields: DocumentField[];
  signature: { method: 'DRAWN' | 'UPLOADED'; updatedAt: string } | null;
  canSaveTemplateDefaults: boolean;
}

export type InvoiceType = 'USD' | 'LKR';

export interface Invoice {
  id: number;
  invoiceNumber: string;
  studentId: number;
  paymentId: number | null;
  amount: string;
  description: string | null;
  issuedAt: string;
  currency: { id: number; code: string; symbol: string | null };
  issuedBy: { firstName: string; lastName: string };
  // Formatted USD/LKR Invoice — null for every invoice generated by either
  // pre-existing path (Payment Checklist / manual amount+description).
  invoiceType: InvoiceType | null;
  bankCharges: string | null;
  exchangeRate: string | null;
  exchangeRateDate: string | null;
  /** Phase 104 — where the snapshotted rate came from (null on older invoices). */
  exchangeRateOrigin?: 'AUTOMATIC' | 'MANUAL' | null;
}

/** Phase 104 — an invoice's figures before it is generated. */
export interface InvoicePreview {
  invoiceType: InvoiceType | null;
  currency: string | null;
  lineItems: Array<{ description: string; amountUsd: number; taxUsd: number | null; lkrAmount: number | null }>;
  subtotalUsd: number;
  bankCharges: number | null;
  totalUsd: number;
  totalLkr: number | null;
  exchangeRate: number | null;
  exchangeRateDate: string | null;
  exchangeRateId: number | null;
  exchangeRateOrigin: 'AUTOMATIC' | 'MANUAL' | null;
}

export interface CreateStudentPayload {
  leadId?: number;
  /** Creates the student in draft (isDraft=true) — used by the wizard's Step 1 autosave. */
  saveAsDraft?: boolean;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  dateOfBirth?: string;
  gender?: Gender;
  countryId?: number;
  branchId?: number;
  address?: string;
  passportNumber?: string;
  passportExpiryDate?: string;
  fatherName?: string;
  fatherPhone?: string;
  fatherOccupation?: string;
  fatherOrganization?: string;
  fatherOfficePhone?: string;
  fatherEmail?: string;
  motherName?: string;
  motherPhone?: string;
  motherOccupation?: string;
  motherOrganization?: string;
  motherOfficePhone?: string;
  motherEmail?: string;
  guardianName?: string;
  guardianRelationship?: string;
  guardianPhone?: string;
  guardianEmail?: string;
  emergencyContactName?: string;
  emergencyContactPhone?: string;
  emergencyContactRelationship?: string;

  otherNames?: string;
  preferredName?: string;
  nicNumber?: string;
  passportIssueDate?: string;
  countryOfBirthId?: number;
  maritalStatus?: MaritalStatus;
  whatsappNumber?: string;
  telephone?: string;
  city?: string;
  postalCode?: string;
  province?: string;
  nationalityCountryId?: number;

  schoolName?: string;
  olType?: OlAlType;
  olYear?: number;
  olResults?: SubjectResult[];
  alType?: OlAlType;
  alYear?: number;
  alStream?: ALStream;
  alResults?: SubjectResult[];
  overallGpa?: number;

  preferredUniversityId?: number;
  preferredProgramId?: number;
  preferredFacultyId?: number;
  preferredQualificationLevelId?: number;
  preferredIntakeId?: number;
  preferredIntakeLabel?: string;
  preferredCity?: string;
  alternativeCountryId?: number;
  alternativeUniversityId?: number;
  budget?: number;
  budgetCurrencyId?: number;
  scholarshipRequired?: boolean;
  englishTestType?: EnglishTestType;
  englishTestScore?: string;
  englishTestDate?: string;

  fatherWhatsapp?: string;
  fatherAddress?: string;
  fatherIsSponsor?: boolean;
  motherWhatsapp?: string;
  motherAddress?: string;
  motherIsSponsor?: boolean;
  guardianWhatsapp?: string;
  guardianAddress?: string;
  guardianIsSponsor?: boolean;

  visaAppliedBefore?: boolean;
  visaRefused?: boolean;
  visaRefusedCountryId?: number;
  visaType?: VisaType;
  visaRefusalReason?: string;
  visaComments?: string;
  travelHistory?: string;

  monthlyIncome?: number;
  annualIncome?: number;
  fundingMethod?: FundingSource;
  sponsorDetails?: string;

  studentAgreementAccepted?: boolean;
  privacyConsentAccepted?: boolean;
  marketingConsentAccepted?: boolean;
  termsAcceptedAt?: string;
  studentSignatureName?: string;
  studentSignatureAt?: string;
  guardianSignatureName?: string;
  guardianSignatureAt?: string;

  healthDeclarationRequired?: boolean;
  healthDeclarationAnswers?: HealthDeclarationAnswer[];
  guardianPassportOrNic?: string;

  campaignId?: number;
  academicYear?: string;
  semester?: string;
  lastCompletedStep?: number;
}

export type UpdateStudentPayload = Omit<CreateStudentPayload, 'leadId' | 'saveAsDraft'>;

export interface TimelineApplication {
  id: number;
  applicationNumber: string;
  status: ApplicationStatus;
  university: { name: string };
  course: { name: string };
  createdBy: { id: number; firstName: string; lastName: string };
}

export interface TimelinePayment {
  id: number;
  paymentNumber: string;
  amount: string;
  status: PaymentStatus;
  category: { name: string };
  currency: { code: string; symbol: string | null };
  recordedBy: { id: number; firstName: string; lastName: string };
  approvedBy: { id: number; firstName: string; lastName: string } | null;
  receipt: { id: number; receiptNumber: string; issuedAt: string } | null;
}

/** Timeline correction (Phase 58) — every structured domain event already
 * written to audit_logs via recordAuditEvent for this student (fee change
 * request/approve/reject, the Master Data fee sync, document approve/
 * reject/generate/send/download, lead conversion), reused as-is rather
 * than a second logging system. oldValue/newValue are the same JSON
 * payloads recorded at the time — shape varies by `action`, so each is
 * destructured defensively (optional chaining, never assumed present)
 * in StudentTimelineTab.tsx's own per-action renderer. */
export type TimelineAuditAction =
  | 'CONVERSION_BLOCKED'
  | 'CONVERTED'
  | 'DOCUMENT_APPROVED'
  | 'DOCUMENT_REJECTED'
  | 'DOCUMENT_GENERATED'
  | 'DOCUMENT_REGENERATED'
  | 'DOCUMENT_SENT'
  | 'DOCUMENT_DOWNLOADED'
  | 'FEE_CHANGE_REQUESTED'
  | 'FEE_CHANGE_APPROVED'
  | 'FEE_CHANGE_REJECTED'
  | 'FEE_SYNCED_FROM_DEFAULT'
  | 'ASSIGNED'
  | 'TRANSFERRED'
  | 'EDITED'
  | 'STATUS_CHANGED'
  | 'DELETED'
  // Student Portal (Phase 67)
  | 'CHANGE_REQUESTED'
  | 'CHANGE_APPROVED'
  | 'CHANGE_REJECTED'
  // Digital Signature (Phase 78)
  | 'SIGNATURE_CREATED'
  | 'SIGNATURE_REPLACED'
  | 'SIGNATURE_APPLIED'
  // Intake/batch transfers (Phase 94)
  | 'INTAKE_TRANSFER_REQUESTED'
  | 'INTAKE_TRANSFER_APPROVED'
  | 'INTAKE_TRANSFER_REJECTED'
  // Student Portal login management (Phase 100)
  | 'PORTAL_ACCOUNT_CREATED'
  | 'PORTAL_INVITE_SENT'
  | 'PORTAL_PASSWORD_RESET_SENT'
  | 'PORTAL_LOGIN_EMAIL_CHANGED'
  | 'PORTAL_ACCOUNT_DISABLED'
  | 'PORTAL_ACCOUNT_ENABLED'
  // Official dropout (Management Reports)
  | 'STUDENT_DROPOUT_RECORDED'
  | 'STUDENT_DROPOUT_REVERSED';

export interface TimelineAuditEvent {
  action: TimelineAuditAction;
  performedBy: { id: number; firstName: string; lastName: string; role: { name: string } } | null;
  oldValue: unknown;
  newValue: unknown;
}

export type StudentTimelineEntry =
  | { kind: 'DOCUMENT_UPLOADED'; at: string; data: StudentDocument }
  | { kind: 'QUALIFICATION_ADDED'; at: string; data: Qualification }
  | { kind: 'APPLICATION_CREATED'; at: string; data: TimelineApplication }
  | { kind: 'PAYMENT_RECORDED'; at: string; data: TimelinePayment }
  | { kind: 'PAYMENT_DECIDED'; at: string; data: TimelinePayment }
  | { kind: 'RECEIPT_ISSUED'; at: string; data: TimelinePayment }
  | { kind: 'AUDIT_EVENT'; at: string; data: TimelineAuditEvent };
